import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createContactMessage,
  getContactContent,
} from "../../actions/contactActions";
import { CONTACT_CREATE_RESET } from "../../constants/contactConstants";
import Loader from "../../components/Loader";
import Meta from "../../components/Meta";
import Message from "../../components/Message";
import "./ContactScreen.css";

const ContactScreen = () => {
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(() => {
    const reference = new URLSearchParams(window.location.search).get("orderNumber");
    return reference ? `Order ${reference.slice(0, 80)}` : "";
  });
  const [message, setMessage] = useState("");

  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
    contact: contactCreate,
  } = useSelector((state) => state.contactCreate);

  const {
    loading: loadingContent,
    error: errorContent,
    content,
  } = useSelector((state) => state.contactContentDetails);

  useEffect(() => {
    dispatch(getContactContent());
  }, [dispatch]);

  useEffect(() => {
    if (successCreate) {
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");

      const timer = setTimeout(() => {
        dispatch({ type: CONTACT_CREATE_RESET });
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [dispatch, successCreate]);

  const submitHandler = (e) => {
    e.preventDefault();

    dispatch(
      createContactMessage(
        name.trim(),
        email.trim(),
        subject.trim(),
        message.trim(),
      ),
    );
  };

  return (
    <main className="contact-magazine">
      <Meta
        title="Contact Academy Costumes London"
        description="Contact Academy Costumes London for costume production, bespoke tailoring, vintage textiles, haberdashery, jewellery hire and appointment enquiries."
        image="/images/home/academy-costumes.png"
        url="/contact"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Contact", url: "/contact" },
        ]}
      />

      <div className="contact-magazine__inner">
        <header className="contact-magazine__masthead">
          <h1 className="contact-magazine__title">
            {content?.title || "Contact"}
          </h1>

          <p className="contact-magazine__standfirst">
            {content?.intro ||
              "Get in touch regarding costume production, rentals, appointments, or general enquiries. We would be delighted to hear from you."}
          </p>
        </header>

        {loadingContent ? (
          <div className="contact-magazine__state">
            <Loader />
          </div>
        ) : errorContent ? (
          <div className="contact-magazine__state">
            <Message variant="danger">{errorContent}</Message>
          </div>
        ) : (
          <section className="contact-magazine__spread">
            <article className="contact-magazine__panel contact-magazine__panel--form">
              <div className="contact-magazine__panelHeader">
                <span className="contact-magazine__panelKicker">
                  Write to us
                </span>

                <h2 className="contact-magazine__panelTitle">
                  {content?.formTitle || "Contact Form"}
                </h2>
              </div>

              {errorCreate ? (
                <div className="contact-magazine__alert contact-magazine__alert--error">
                  {errorCreate}
                </div>
              ) : null}

              {successCreate ? (
                <div className="contact-magazine__alert contact-magazine__alert--success">
                  {contactCreate?.message ||
                    "Your message has been sent successfully."}
                </div>
              ) : null}

              <form className="contact-magazine__form" onSubmit={submitHandler}>
                <div className="contact-magazine__field">
                  <label className="contact-magazine__label" htmlFor="name">
                    Your Name *
                  </label>

                  <input
                    id="name"
                    className="contact-magazine__input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                    required
                  />
                </div>

                <div className="contact-magazine__field">
                  <label className="contact-magazine__label" htmlFor="email">
                    Your Email *
                  </label>

                  <input
                    id="email"
                    className="contact-magazine__input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="contact-magazine__field">
                  <label className="contact-magazine__label" htmlFor="subject">
                    Subject
                  </label>

                  <input
                    id="subject"
                    className="contact-magazine__input"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>

                <div className="contact-magazine__field">
                  <label className="contact-magazine__label" htmlFor="message">
                    Message *
                  </label>

                  <textarea
                    id="message"
                    className="contact-magazine__textarea"
                    rows="7"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="contact-magazine__submit"
                  disabled={loadingCreate}
                >
                  {loadingCreate ? "Sending..." : "Send Message"}
                </button>
              </form>
            </article>

            <aside className="contact-magazine__panel contact-magazine__panel--details">
              <div className="contact-magazine__panelHeader">
                <span className="contact-magazine__panelKicker">
                  Information
                </span>

                <h2 className="contact-magazine__panelTitle">
                  {content?.detailsTitle || "Contact Details"}
                </h2>
              </div>

              <div className="contact-magazine__details">
                {content?.address ? (
                  <div className="contact-magazine__detailBlock">
                    <h3>Address</h3>
                    <p>{content.address}</p>
                  </div>
                ) : null}

                {content?.phone ? (
                  <div className="contact-magazine__detailBlock">
                    <h3>Telephone</h3>
                    <p>{content.phone}</p>
                  </div>
                ) : null}

                {content?.email ? (
                  <div className="contact-magazine__detailBlock">
                    <h3>Email</h3>
                    <p>{content.email}</p>
                  </div>
                ) : null}

                {content?.hours ? (
                  <div className="contact-magazine__detailBlock">
                    <h3>Opening Hours</h3>
                    <p>{content.hours}</p>
                  </div>
                ) : null}
              </div>
            </aside>
          </section>
        )}
      </div>
    </main>
  );
};

export default ContactScreen;
